import { Search, SlidersHorizontal, Crosshair } from "lucide-react";
import { CATEGORIES, getCategoryLabel, QUALITIES } from "../lib/api";
import { useI18n } from "../i18n/I18nProvider";

export default function MarketplaceFilters({
  filters,
  center,
  onChange,
  onReset,
  onUseMyLocation,
  onClearLocation,
}) {
  const { t, lang } = useI18n();
  const { category, quality, location, search, pickup, delivery, radius } = filters;

  return (
    <aside className="col-span-12 md:col-span-3 bg-[var(--bone)] p-6">
      <div className="flex items-center gap-2 label-tech mb-4"><SlidersHorizontal size={14} /> {t("market.filters")}</div>

      <div className="mb-6">
        <div className="label-tech mb-2">{t("market.search")}</div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-500)]" />
          <input
            type="text"
            className="input-tech pl-8"
            placeholder={t("market.searchPh")}
            value={search}
            onChange={(e) => onChange("search", e.target.value)}
            data-testid="filter-search-input"
          />
        </div>
      </div>

      <div className="mb-6">
        <div className="label-tech mb-2">{t("market.category")}</div>
        <div className="flex flex-col gap-1">
          <button onClick={() => onChange("category", "All")} className={`text-left px-3 py-1.5 text-sm border ${category === "All" ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]" : "bg-transparent border-transparent hover:border-[var(--ink-200)]"}`} data-testid="filter-category-All">{t("market.allCategories")}</button>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => onChange("category", c)} className={`text-left px-3 py-1.5 text-sm border ${category === c ? "bg-[var(--ink)] text-[var(--bone)] border-[var(--ink)]" : "bg-transparent border-transparent hover:border-[var(--ink-200)]"}`} data-testid={`filter-category-${c}`}>
              {getCategoryLabel(c, lang)}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <div className="label-tech mb-2">{t("market.quality")}</div>
        <select className="input-tech" value={quality} onChange={(e) => onChange("quality", e.target.value)} data-testid="filter-quality-select">
          <option value="">{t("market.any")}</option>
          {QUALITIES.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
      </div>

      <div className="mb-6">
        <div className="label-tech mb-2">{t("market.locationText")}</div>
        <input type="text" className="input-tech" placeholder={t("market.locationPh")} value={location} onChange={(e) => onChange("location", e.target.value)} data-testid="filter-location-input" />
      </div>

      <div className="mb-6">
        <div className="label-tech mb-2">{t("market.distance")}</div>
        {!center ? (
          <button onClick={onUseMyLocation} className="btn-ghost w-full justify-center" data-testid="use-my-location-btn">
            <Crosshair size={14} /> {t("market.useMyLocation")}
          </button>
        ) : (
          <div>
            <div className="label-tech mb-2 text-[var(--brick)]">{t("market.within")} {radius || 25} {t("market.kmFromYou")}</div>
            <input
              type="range"
              min="1"
              max="200"
              value={radius || 25}
              onChange={(e) => onChange("radius", Number(e.target.value))}
              className="w-full accent-[var(--brick)]"
              data-testid="radius-slider"
            />
            <button onClick={onClearLocation} className="label-tech mt-2 hover:text-[var(--ink)] underline" data-testid="clear-location-btn">{t("market.clearLocation")}</button>
          </div>
        )}
      </div>

      <div className="mb-2">
        <div className="label-tech mb-2">{t("market.handover")}</div>
        <label className="flex items-center gap-2 text-sm py-1 cursor-pointer">
          <input type="checkbox" checked={pickup} onChange={(e) => onChange("pickup", e.target.checked)} data-testid="filter-pickup-checkbox" />
          {t("market.pickupAvailable")}
        </label>
        <label className="flex items-center gap-2 text-sm py-1 cursor-pointer">
          <input type="checkbox" checked={delivery} onChange={(e) => onChange("delivery", e.target.checked)} data-testid="filter-delivery-checkbox" />
          {t("market.deliveryAvailable")}
        </label>
      </div>

      <button onClick={onReset} className="btn-ghost mt-4 w-full justify-center" data-testid="filter-reset-btn">{t("market.reset")}</button>
    </aside>
  );
}
